import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Clock, ArrowRight } from 'lucide-react';

const POLL_INTERVAL = 30000; // 30 seconds

export default function SLABreachBadge() {
  const [breachData, setBreachData] = useState({ breached: 0, escalated: 0 });
  const [breachedItems, setBreachedItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchBreachCount = useCallback(async () => {
    try {
      const { data } = await api.get('/approvals/sla-breach-count');
      setBreachData(data);
    } catch {}
  }, []);

  const fetchBreachedItems = useCallback(async () => {
    try {
      const { data } = await api.get('/approvals/pending');
      const breached = (data || []).filter(a => a.sla_breached);
      setBreachedItems(breached.slice(0, 10));
    } catch {}
  }, []);

  useEffect(() => {
    fetchBreachCount();
    const interval = setInterval(fetchBreachCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchBreachCount]);

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) fetchBreachedItems();
  };

  if (breachData.breached === 0) return null;

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="sla-breach-bell"
          className="relative rounded-sm h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50"
        >
          <AlertTriangle className="h-4 w-4" />
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-[#E11D48] text-white text-[9px] font-bold flex items-center justify-center px-1 animate-pulse"
            data-testid="sla-breach-count"
          >
            {breachData.breached > 99 ? '99+' : breachData.breached}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="right"
        sideOffset={8}
        className="w-80 p-0 rounded-sm border border-red-200 shadow-lg"
        data-testid="sla-breach-panel"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-red-100 bg-red-50/50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-bold text-red-800 font-heading">SLA Breaches</h3>
          </div>
          <div className="flex gap-3 mt-1.5">
            <div className="flex items-center gap-1">
              <span className="text-lg font-mono font-bold text-red-700">{breachData.breached}</span>
              <span className="text-[10px] uppercase tracking-wider text-red-500">breached</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-lg font-mono font-bold text-amber-600">{breachData.escalated}</span>
              <span className="text-[10px] uppercase tracking-wider text-amber-500">escalated</span>
            </div>
          </div>
        </div>

        {/* Breached Items */}
        <div className="max-h-64 overflow-y-auto">
          {breachedItems.length === 0 ? (
            <div className="py-8 text-center">
              <Clock className="h-6 w-6 mx-auto text-slate-200" />
              <p className="text-xs text-slate-400 mt-2">Loading breached approvals...</p>
            </div>
          ) : (
            <div className="divide-y divide-red-50">
              {breachedItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { navigate(`/invoices/${item.invoice_id}`); setOpen(false); }}
                  data-testid={`sla-item-${item.invoice_number}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-red-50/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-slate-800">{item.invoice_number}</span>
                      <Badge variant="outline" className="rounded-sm text-[8px] uppercase tracking-wider border-red-300 text-red-600 px-1 py-0">
                        {item.approval_tier}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">{item.vendor_name}</p>
                    <p className="text-[10px] text-red-400 font-mono mt-0.5">
                      Due: {item.sla_deadline ? new Date(item.sla_deadline).toLocaleString() : '—'}
                    </p>
                  </div>
                  <ArrowRight className="h-3 w-3 text-red-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-red-100 bg-red-50/30">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { navigate('/approvals'); setOpen(false); }}
            data-testid="view-all-breaches-button"
            className="w-full text-xs text-red-600 hover:text-red-800 hover:bg-red-100 rounded-sm"
          >
            View All Approvals
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
